import React, {useState} from 'react';
import {Button} from "primereact/button";
import {Badge} from "primereact/badge";
import {Skeleton} from "primereact/skeleton";
import {statusMeta} from "./onboardingConfig";

// Short relative-time string for "last updated Xs ago".
function sinceLabel(lastUpdated, t) {
    if (!lastUpdated) return '';
    const secs = Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000));
    if (secs < 60) return t('onboarding.monitor.secsAgo', `${secs}sn önce`).replace('{s}', secs);
    const mins = Math.round(secs / 60);
    return t('onboarding.monitor.minsAgo', `${mins}dk önce`).replace('{m}', mins);
}

// Per-step compact summary line shown under each row.
function stepDetail(step, t) {
    const s = step.summary || {};
    switch (step.key) {
        case 'products':
            return t('onboarding.monitor.products', `${s.count || 0} ürün hazır`).replace('{n}', s.count || 0);
        case 'sites':
            return t('onboarding.monitor.sites', `${s.selected || 0} seçildi · ${s.ready || 0} hazır · ${s.needs_prep || 0} hazırlık gerekiyor`)
                .replace('{selected}', s.selected || 0).replace('{ready}', s.ready || 0).replace('{prep}', s.needs_prep || 0);
        case 'prepare':
            return t('onboarding.monitor.prepare', `${s.building || 0} üretiliyor · ${s.ready || 0} hazır · ${s.failed || 0} hata · ${s.needs_prep || 0} bekliyor`)
                .replace('{building}', s.building || 0).replace('{ready}', s.ready || 0).replace('{failed}', s.failed || 0).replace('{prep}', s.needs_prep || 0);
        case 'search':
            return t('onboarding.monitor.search', `${s.done || 0}/${s.total || 0} tarama tamamlandı`)
                .replace('{done}', s.done || 0).replace('{total}', s.total || 0);
        case 'match':
            return t('onboarding.monitor.match', `${s.auto_matched || 0} otomatik · ${s.pending_review || 0} onay bekliyor`)
                .replace('{auto}', s.auto_matched || 0).replace('{pending}', s.pending_review || 0);
        case 'map':
            return t('onboarding.monitor.map', `${s.with_map || 0}/${s.products || 0} ürün MAP'li · ${s.missing_map || 0} eksik`)
                .replace('{with}', s.with_map || 0).replace('{products}', s.products || 0).replace('{missing}', s.missing_map || 0);
        case 'pricing':
            return s.configured
                ? t('onboarding.monitor.pricingOn', 'Dinamik fiyatlandırma aktif')
                : t('onboarding.monitor.pricingOff', 'Dinamik fiyatlandırma yapılandırılmadı');
        default:
            return '';
    }
}

// Expandable per-site sub-rows (prepare + sites steps).
function SiteSubRows({sites, t}) {
    if (!sites || sites.length === 0) return null;
    const subStatusLabel = {
        ready: t('onboarding.site.ready', 'hazır'),
        needs_site_search: t('onboarding.site.needsSearch', 'site-içi arama gerekiyor'),
        needs_spider: t('onboarding.site.needsSpider', 'spider gerekiyor'),
        ai_building: t('onboarding.site.building', 'AI üretiyor'),
        ai_failed: t('onboarding.site.failed', 'AI başarısız'),
        searching: t('onboarding.site.searching', 'aranıyor'),
        searched: t('onboarding.site.searched', 'arandı'),
        matched: t('onboarding.site.matched', 'eşleşti'),
    };
    return (
        <ul style={{listStyle: 'none', margin: '6px 0 0', padding: '0 0 0 26px'}}>
            {sites.map((site, idx) => (
                <li key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '2px 0', color: '#555'}}>
                    <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%'}}>{site.host || site.url}</span>
                    <span style={{color: site.is_site_searching_ready ? '#22c55e' : '#f59e0b'}}>
                        {subStatusLabel[site.sub_status] || site.sub_status}
                    </span>
                </li>
            ))}
        </ul>
    );
}

const OnboardingMonitor = ({status, loading, lastUpdated, onRefresh, t, currentStep, onStepClick, steps = []}) => {
    const [expanded, setExpanded] = useState({});
    const toggle = (key) => setExpanded(prev => ({...prev, [key]: !prev[key]}));

    const stepByKey = {};
    (status?.steps || []).forEach(s => {
        stepByKey[s.key] = s;
    });

    return (
        <div className="onboarding-monitor" style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                <strong>{t('onboarding.monitor.title', 'Canlı İzleme')}</strong>
                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <small style={{color: '#9ca3af'}}>
                        {lastUpdated ? t('onboarding.monitor.updated', 'son güncelleme: ') + sinceLabel(lastUpdated, t) : ''}
                    </small>
                    <Button icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={onRefresh} disabled={loading}
                            tooltip={t('onboarding.monitor.refresh', 'Yenile')}/>
                </span>
            </div>

            {loading ? (
                <>
                    {(steps.length ? steps : [{key: 's1'}, {key: 's2'}, {key: 's3'}, {key: 's4'}]).map(s => (
                        <div key={s.key} style={{marginBottom: 14}}>
                            <Skeleton width="40%" height="1rem" className="p-mb-1"/>
                            <Skeleton width="70%" height="0.8rem"/>
                        </div>
                    ))}
                </>
            ) : (
                steps.map(stepDef => {
                    const step = stepByKey[stepDef.key] || {key: stepDef.key, status: 'pending', summary: {}};
                    const meta = statusMeta(step.status);
                    const expandable = (stepDef.key === 'prepare' || stepDef.key === 'sites') && (step.summary?.sites || []).length > 0;
                    const isCurrent = currentStep === stepDef.key;
                    return (
                        <div key={stepDef.key}
                             style={{padding: '8px 6px', borderRadius: 8, marginBottom: 4, cursor: onStepClick ? 'pointer' : 'default',
                                 background: isCurrent ? '#eff6ff' : 'transparent'}}
                             onClick={() => onStepClick && onStepClick(stepDef.key)}>
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <i className={meta.icon} style={{color: meta.color}}/>
                                    <span style={{fontWeight: isCurrent ? 600 : 500}}>{t(stepDef.labelKey, stepDef.labelDefault)}</span>
                                </span>
                                {expandable && (
                                    <Button icon={expanded[stepDef.key] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                            className="p-button-text p-button-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggle(stepDef.key);
                                            }}/>
                                )}
                            </div>
                            <div style={{fontSize: '0.82rem', color: '#6b7280', paddingLeft: 26}}>{stepDetail(step, t)}</div>
                            {expandable && expanded[stepDef.key] && <SiteSubRows sites={step.summary.sites} t={t}/>}
                        </div>
                    );
                })
            )}

            {status?.state === 'ready' && (
                <div style={{marginTop: 12, textAlign: 'center'}}>
                    <Badge value={t('onboarding.monitor.ready', 'Kurulum tamamlandı 🎉')} severity="success"/>
                </div>
            )}
        </div>
    );
};

export default OnboardingMonitor;
